import React from 'react';
import { MdClose, MdAccessTime } from "react-icons/md";
import { format } from 'date-fns';

const VolunterLogsModal = ({ isOpen, onClose, personName, eventName, logsData }) => {
    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Format date function
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return format(date, 'dd MMM yyyy • h:mm a');
        } catch (error) {
            return dateString;
        }
    };

    // Get profile URL from log
    const getProfileUrl = (log) => {
        return log?.attendee?.profile_url ||
            log?.performed_by?.profile_url ||
            "https://static.vecteezy.com/system/resources/thumbnails/009/292/244/small/default-avatar-icon-of-social-media-user-vector.jpg";
    };

    // Get attendee name from log
    const getAttendeeName = (log) => {
        return log?.attendee?.name || log?.performed_by?.name || 'Unknown';
    };

    // Get attendee email from log
    const getAttendeeEmail = (log) => {
        return log?.attendee?.email || '';
    };

    // Get performed by name
    const getPerformedByName = (log) => {
        if (log?.performed_by?.name) {
            return log.performed_by.name;
        }
        // Extract name from created_by if it's in format "Name<email>"
        if (log?.created_by) {
            const match = log.created_by.match(/^([^<]+)/);
            return match ? match[1].trim() : log.created_by;
        }
        return 'Unknown';
    };

    // Get attendance status
    const getAttendanceStatus = (log) => {
        return log?.attendance_status || log?.action || 'UNKNOWN';
    };

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED':
                return '#10B981'; // Green
            case 'REJECTED':
                return '#EF4444'; // Red
            case 'PENDING':
                return '#F59E0B'; // Orange
            case 'CHECKED_IN':
                return '#0044C6'; // Blue
            case 'CHECKED_OUT':
                return '#6F7D95'; // Gray
            default:
                return '#6F7D95'; // Gray
        }
    };

    // Check if logsData exists and has items
    const hasLogs = logsData && Array.isArray(logsData) && logsData.length > 0;

    return (
        <div
            onClick={handleOverlayClick}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1100,
                backdropFilter: "blur(5px)",
            }}
        >
            <div
                style={{
                    background: "#FFFFFF",
                    borderRadius: "20px",
                    width: "90%",
                    maxWidth: "500px",
                    maxHeight: "85vh",
                    overflow: "hidden",
                    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                    animation: "modalSlideIn 0.3s ease",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Modal Header */}
                <div
                    style={{
                        padding: "20px",
                        background: "#0044C6",
                        color: "white",
                        position: "relative",
                    }}
                >
                    <h3 style={{
                        margin: 0,
                        fontSize: "1.25rem",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "white"
                    }}>
                        <MdAccessTime /> Access Logs
                    </h3>
                    <p style={{
                        margin: "8px 0 0 0",
                        fontSize: "0.85rem",
                        opacity: 0.9,
                        color: "white"
                    }}>
                        {personName} • {eventName}
                    </p>

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        style={{
                            position: "absolute",
                            top: "16px",
                            right: "16px",
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "50%",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            cursor: "pointer",
                            color: "white",
                            backdropFilter: "blur(5px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                        }}
                    >
                        <MdClose />
                    </button>
                </div>

                {/* Modal Body - Logs List */}
                <div
                    style={{
                        padding: "20px",
                        overflowY: "auto",
                        flex: 1,
                        background: "#FFFFFF",
                    }}
                >
                    {hasLogs ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxHeight: "60vh", overflowY: "scroll", scrollbarWidth: "none" }}>
                            {logsData.map((log, index) => (
                                <div
                                    key={log.id || index}
                                    style={{
                                        borderBottom: index < logsData.length - 1 ? "1px solid #E9EDF4" : "none",
                                        paddingBottom: index < logsData.length - 1 ? "20px" : "0",
                                        display: "flex",
                                        gap: "12px",
                                    }}
                                >
                                    {/* Profile Image */}
                                    <div style={{
                                        width: "48px",
                                        height: "48px",
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        background: "#E9EDF4",
                                        flexShrink: 0,
                                        border: "2px solid #0044C6",
                                    }}>
                                        <img
                                            src={getProfileUrl(log)}
                                            alt={getAttendeeName(log)}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                            onError={(e) => {
                                                e.target.src = "https://static.vecteezy.com/system/resources/thumbnails/009/292/244/small/default-avatar-icon-of-social-media-user-vector.jpg";
                                            }}
                                        />
                                    </div>

                                    {/* Content */}
                                    <div style={{ flex: 1 }}>
                                        {/* Name and Status Row */}
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "4px"
                                        }}>
                                            <div style={{
                                                fontSize: "1rem",
                                                fontWeight: 600,
                                                color: "#0044C6"
                                            }}>
                                                {getAttendeeName(log)}
                                            </div>

                                            {/* Status Badge */}
                                            <span
                                                style={{
                                                    padding: "2px 8px",
                                                    borderRadius: "12px",
                                                    fontSize: "0.7rem",
                                                    fontWeight: 600,
                                                    textTransform: "uppercase",
                                                    backgroundColor: getStatusColor(getAttendanceStatus(log)),
                                                    color: "white",
                                                    letterSpacing: "0.3px",
                                                }}
                                            >
                                                {getAttendanceStatus(log)}
                                            </span>
                                        </div>

                                        {/* Email */}
                                        {getAttendeeEmail(log) && (
                                            <div style={{
                                                fontSize: "0.8rem",
                                                color: "#6F7D95",
                                                marginBottom: "8px"
                                            }}>
                                                {getAttendeeEmail(log)}
                                            </div>
                                        )}

                                        {/* Performed By */}
                                        <div style={{
                                            fontSize: "0.85rem",
                                            color: "#0B1C33"
                                        }}>
                                            <span style={{ color: "#6F7D95" }}>Performed By : </span>
                                            <span style={{ fontWeight: 500 }}>{getPerformedByName(log)}</span>
                                        </div>

                                        {/* Date and Time */}
                                        <div style={{
                                            fontSize: "0.8rem",
                                            color: "#6F7D95",
                                            marginTop: "2px"
                                        }}>
                                            {formatDate(log.created_at)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        // No Logs State
                        <div
                            style={{
                                textAlign: "center",
                                padding: "40px 20px",
                                background: "white",
                                borderRadius: "12px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <MdAccessTime style={{ fontSize: "48px", color: "#E9EDF4", marginBottom: "12px" }} />
                            <h4 style={{ margin: "0 0 8px 0", color: "#0B1C33", fontWeight: 600 }}>No Logs Found</h4>
                            <p style={{ margin: 0, color: "#6F7D95", fontSize: "0.9rem" }}>
                                No access logs available for this volunteer.
                            </p>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div
                    style={{
                        padding: "16px 20px",
                        borderTop: "1px solid #E9EDF4",
                        background: "white",
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <button
                        onClick={onClose}
                        style={{
                            padding: "8px 24px",
                            background: "#0044C6",
                            color: "white",
                            border: "none",
                            borderRadius: "30px",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,68,198,0.3)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Add animation keyframes */}
            <style>
                {`
                    @keyframes modalSlideIn {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default VolunterLogsModal;